// Built-in avatars offered in Environment Settings. `id` is what gets saved
// (localStorage and the participant's `selectedAvatar` in Firestore), so it is
// the assistant's name in lowercase; the model file behind it can change
// without changing the saved value. `body` is the TalkingHead
// body type ('F' / 'M'), which drives posture and gesture animations;
// `voiceGender` is applied when the avatar is chosen so the voice matches.
// `assistantName` is what the assistant calls itself everywhere (greeting,
// tour, Home, backend prompts); avatars without one use DEFAULT_ASSISTANT_NAME.
//
// Local models live in frontend/public/avatars and are served from /avatars
// (App Engine: see the /avatars handler in frontend/app*.yaml).
export const BUILT_IN_AVATARS = [
  {
    id: 'jenny',
    label: 'Jenny',
    assistantName: 'Jenny',
    url: 'https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@main/avatars/avaturn.glb',
    body: 'F',
    voiceGender: 'female',
  },
  {
    id: 'xavier',
    label: 'Xavier',
    assistantName: 'Xavier',
    // Avaturn export with its Neck/Head rest pose adjusted so TalkingHead's
    // gaze heuristic doesn't make him stare downward; see
    // frontend/scripts/fix-avatar-rest-pose.py.
    // The ?v= query busts browser/CDN caches when the model file changes.
    url: '/avatars/assistant-male.glb?v=2',
    body: 'M',
    voiceGender: 'male',
    // TalkingHead drives the eyeLookDown morph from the head's pitch (which
    // wanders with its random head moves). On this model that morph is much
    // stronger than on Jenny and also drags the eyelids down, so he looks
    // like he is staring at the floor. Pin it to 0, and lift the eyes slightly
    // (eyesLookUp 0.15) because his irises sit low in the socket at rest.
    // He still glances sideways and blinks.
    fixedMorphs: { eyesLookDown: 0, eyesLookUp: 0.15 },
  },
  // Three more Avaturn exports (source files: Figure_for_Avatar/). The two men
  // share Xavier's rig, so they get the same rest-pose patch (delta 0.22) and
  // eye settings; the woman shares Jenny's rig and needs neither.
  {
    id: 'maya',
    label: 'Maya',
    assistantName: 'Maya',
    url: '/avatars/african-american-female.glb?v=3',
    body: 'F',
    voiceGender: 'female',
  },
  {
    id: 'marcus',
    label: 'Marcus',
    assistantName: 'Marcus',
    url: '/avatars/african-american-male.glb?v=2',
    body: 'M',
    voiceGender: 'male',
    fixedMorphs: { eyesLookDown: 0, eyesLookUp: 0.15 },
  },
  {
    id: 'daniel',
    label: 'Daniel',
    assistantName: 'Daniel',
    url: '/avatars/caucasian-male.glb?v=2',
    body: 'M',
    voiceGender: 'male',
    fixedMorphs: { eyesLookDown: 0, eyesLookUp: 0.15 },
  },
  {
    id: 'sophia',
    label: 'Sophia',
    assistantName: 'Sophia',
    url: 'https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@main/avatars/brunette.glb',
    body: 'F',
    voiceGender: 'female',
  },
];

export const DEFAULT_AVATAR_ID = 'jenny';
// Saved when the participant uses an avatar they uploaded themselves (feature
// currently hidden); its file address is kept separately.
export const CUSTOM_AVATAR_ID = 'custom';

export const findAvatarById = (id) =>
  BUILT_IN_AVATARS.find((avatar) => avatar.id === id) || null;

export const isKnownAvatarId = (id) => id === CUSTOM_AVATAR_ID || Boolean(findAvatarById(id));

export const DEFAULT_ASSISTANT_NAME = 'Jenny';

export const getAssistantNameForId = (id) =>
  findAvatarById(id)?.assistantName || DEFAULT_ASSISTANT_NAME;

// The selected avatar ID lives in localStorage. Write it through
// storeSelectedAvatar so the app shell (the avatar on screen and the
// assistant's name) switches right away instead of on the next page load.
export const AVATAR_STORAGE_KEY = 'avatarId';
export const CUSTOM_AVATAR_URL_KEY = 'customAvatarUrl';
export const AVATAR_CHANGED_EVENT = 'vica:avatar-changed';

// The stored ID, or '' when nothing valid is stored.
export const readStoredAvatar = () => {
  try {
    const id = window.localStorage.getItem(AVATAR_STORAGE_KEY) || '';
    return isKnownAvatarId(id) ? id : '';
  } catch {
    return '';
  }
};

export const storeSelectedAvatar = (id) => {
  try {
    window.localStorage.setItem(AVATAR_STORAGE_KEY, id);
  } catch {
    // Storage blocked: the shell still follows the event below.
  }
  window.dispatchEvent(new Event(AVATAR_CHANGED_EVENT));
};

export const readCustomAvatarUrl = () => {
  try {
    return window.localStorage.getItem(CUSTOM_AVATAR_URL_KEY) || '';
  } catch {
    return '';
  }
};

// Pass '' to forget the uploaded avatar.
export const storeCustomAvatarUrl = (url) => {
  try {
    if (url) {
      window.localStorage.setItem(CUSTOM_AVATAR_URL_KEY, url);
    } else {
      window.localStorage.removeItem(CUSTOM_AVATAR_URL_KEY);
    }
  } catch {
    // ignore
  }
};

// What the avatar component needs to load an ID. Unknown IDs, and 'custom'
// without a saved file, fall back to the default avatar.
export const resolveAvatar = (id) => {
  if (id === CUSTOM_AVATAR_ID) {
    const url = readCustomAvatarUrl();
    if (url) {
      return { id, url, body: 'F', fixedMorphs: {} };
    }
  }
  const avatar = findAvatarById(id) || findAvatarById(DEFAULT_AVATAR_ID);
  return { id: avatar.id, url: avatar.url, body: avatar.body, fixedMorphs: avatar.fixedMorphs || {} };
};
